import React, { useState, useContext } from "react";

import {
  Button,
  CssBaseline,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
} from '@mui/material';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ThemeDecor from "../../themes/decor/ThemeDecor";
import { logoForBg } from "../../helpers/logoSrc";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    display: "flex",
    position: "relative",
  },
  leftPanel: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.custom.login.bg,
      padding: theme.spacing(6),
      position: "relative",
      zIndex: 1,
    },
  },
  logo: {
    width: "75%",
    maxWidth: 320,
    marginBottom: theme.spacing(4),
  },
  tagline: {
    // Contraste automático com o fundo temático do painel: branco nos temas
    // escuros (como era), texto escuro nos 7 temas de login.bg claro.
    color: theme.palette.getContrastText(theme.custom.login.bg),
    textAlign: "center",
    opacity: 0.85,
    fontWeight: 300,
    letterSpacing: "0.5px",
  },
  rightPanel: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Fundo temático; com decor ativo fica transparente (body pinta
    // background.default via CssBaseline) e patinhas/estrelas aparecem.
    backgroundColor: theme.custom.decorId ? "transparent" : theme.palette.background.default,
    padding: theme.spacing(4),
    position: "relative",
    zIndex: 1,
  },
  formCard: {
    padding: theme.spacing(5),
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
    width: "100%",
    maxWidth: 400,
    backgroundColor: theme.palette.background.paper,
    position: "relative",
    zIndex: 1,
  },
  mobileLogo: {
    display: "block",
    width: "160px",
    margin: "0 auto",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  textField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  submit: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: "10px",
    fontWeight: 700,
    fontSize: "1rem",
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#FFFFFF",
    boxShadow: "0 4px 14px rgba(243,112,33,0.4)",
    transition: "transform 0.15s, box-shadow 0.15s",
    "&:hover": {
      background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      transform: "translateY(-1px)",
      boxShadow: "0 6px 18px rgba(243,112,33,0.5)",
    },
  },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <ThemeDecor variant="login" />

      <div className={classes.leftPanel}>
        <img src={logoForBg(theme, theme.custom.login.bg)} alt="KR Gráfica, Carimbos e Brindes" className={classes.logo} />
        <Typography variant="h6" className={classes.tagline}>
          Atendimento inteligente para sua empresa
        </Typography>
      </div>

      <div className={classes.rightPanel}>
        <Paper className={classes.formCard} elevation={0}>
          <img src={logoForBg(theme, theme.palette.background.paper)} alt="KR Gráfica, Carimbos e Brindes" className={classes.mobileLogo} />

          <Typography component="h1" variant="h5" className={classes.title}>
            {i18n.t("login.title")}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            Entre com suas credenciais para continuar
          </Typography>

          <form noValidate onSubmit={handlSubmit}>
            <TextField
              className={classes.textField}
              variant="outlined"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
            />
            <TextField
              className={classes.textField}
              variant="outlined"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submit}
            >
              {i18n.t("login.buttons.submit")}
            </Button>
          </form>
        </Paper>
      </div>
    </div>
  );
};

export default Login;
